import {v4 as uuid} from 'uuid';
import {Asset} from '@deviceinsight/ng-ui-api-client';
import {fetchAssetByUrn} from '@deviceinsight/ng-ui-api-client';
import {
	AssetState,
	Button,
	ButtonPressed,
	Condition,
	Device,
	DeviceElement,
	DeviceElementTarget,
	Led,
	LogicOperator,
	Pin,
	Rule,
	RuleConditionState,
	UiCondition,
	UiRule
} from '../../modules/ruleengine/types';
import {
	ASSET_TYPE_PROPERTY,
	EVENTS,
	COUNTER_TRIGGER_MIN_VALUE,
	COUNTER_TRIGGER_MAX_VALUE,
	DURATION_TRIGGER_MIN_VALUE,
	DURATION_TRIGGER_MAX_VALUE,
	CompareOperator,
	ACTION_TYPE_WEBSOCKET
} from '../../modules/ruleengine/config/consts';
import {
	allTargets,
	button1Element,
	button2Element,
	gpioElement,
	lightElement,
	RuleStructure,
	statusElement
} from '../../modules/ruleengine/config/rulestructure';
import {mapApiActionToUiAction, mapUiActionToApiAction} from './action/ruleActionHelpers';
import {
	DATAPOINT_BUTTON1,
	DATAPOINT_BUTTON2,
	DATAPOINT_BUTTON_COLOR1,
	DATAPOINT_BUTTON_COLOR2,
	DATAPOINT_IS_ONLINE,
	DATAPOINT_LED,
	DATAPOINT_LED_COLOR,
	DATAPOINT_PIN1,
	DATAPOINT_PIN2,
	DATAPOINT_PIN3,
	DATAPOINT_PIN4
} from '../../modules/common/consts';
export const AssetsCache: Map<string, Asset> = new Map();
export const addAssetToCache = (asset: Asset): void => {
	if (!AssetsCache.get(asset.urn)) AssetsCache.set(asset.urn, asset);
};
export const getAssetFromCacheOrFetch = (urn: string): Promise<Asset> => {
	const cachedAsset = AssetsCache.get(urn);

	if (cachedAsset) {
		return Promise.resolve(cachedAsset);
	} else {
		return fetchAssetByUrn(urn).then((asset) => {
			addAssetToCache(asset);
			return asset;
		});
	}
};
export const getAssetDeviceType = (asset: Asset | null | undefined): string => {
	if (asset) {
		const deviceTypeProperty =
			asset.properties && asset.properties[ASSET_TYPE_PROPERTY]
				? asset.properties[ASSET_TYPE_PROPERTY]
				: 'unknown';
		return typeof deviceTypeProperty === 'string' ? deviceTypeProperty : 'unknown';
	}

	return 'unknown';
};
export const findDeviceForAsset = (asset: Asset): Device | null | undefined => {
	const deviceType = getAssetDeviceType(asset);
	return RuleStructure.devices.find((device) => device.type === deviceType);
};

export const validateRule = (rule: UiRule | null | undefined): boolean => {
	if (!rule) {
		return false;
	}

	const {actions, conditions, triggerConditions, name, apiTriggerEnabled} = rule;

	let hasWebsocketAction = false;
	actions.forEach((action) => {
		if (action.actionType === ACTION_TYPE_WEBSOCKET) {
			hasWebsocketAction = true;
		}
	});

	if (
		actions.length < 1 ||
		(!hasWebsocketAction && actions.length > 10) ||
		(hasWebsocketAction && actions.length > 11)
	) {
		return false;
	}

	if (conditions && !conditions.metadata.isValid) {
		return false;
	}

	if (!triggerConditions && !apiTriggerEnabled) {
		return false;
	}

	if (!name || name.length < 1) {
		return false;
	}

	if (triggerConditions && !triggerConditions.metadata.isValid) {
		return false;
	}

	const invalidAction = actions.find((action) => !action.metadata.isValid);

	if (invalidAction) {
		return false;
	}

	return true;
};
export const getAvailableElements = (asset: Asset): Array<DeviceElement> => {
	const device = findDeviceForAsset(asset);
	if (device) {
		return device.elements;
	} else {
		return []; // unknown device
	}
};
export const validateConditionState = (state: RuleConditionState): boolean => {
	const {asset, elementTarget, online, color, status, pressed, compareOperator, counter, duration} = state;

	if (!asset || !elementTarget || !compareOperator) {
		return false;
	}

	if (counter) {
		if (
			!counter.conditionCount ||
			counter.conditionCount < COUNTER_TRIGGER_MIN_VALUE ||
			counter.conditionCount > COUNTER_TRIGGER_MAX_VALUE
		) {
			return false;
		}
	}

	if (duration) {
		if (
			!duration.durationInMin ||
			duration.durationInMin < DURATION_TRIGGER_MIN_VALUE ||
			duration.durationInMin > DURATION_TRIGGER_MAX_VALUE
		)
			return false;
	}

	switch (elementTarget.dataPoint) {
		case DATAPOINT_LED || DATAPOINT_BUTTON1 || DATAPOINT_BUTTON2:
			return color ? true : false;

		case DATAPOINT_IS_ONLINE:
			return online !== undefined;

		case DATAPOINT_PIN1 || DATAPOINT_PIN2 || DATAPOINT_PIN3 || DATAPOINT_PIN4:
			return status !== undefined;

		case EVENTS.button1Pressed || EVENTS.button2Pressed:
			return pressed !== undefined;
	}

	return true;
};

const findElementTargetByUiCondition = (condition: UiCondition): DeviceElementTarget | void => {
	return allTargets.find((target) => target.dataPoint === condition.dataPoint);
};

const findElementOfDeviceByUiCondition = (device: Device, condition: UiCondition): DeviceElement | void => {
	const {dataPoint} = condition;

	switch (dataPoint) {
		case DATAPOINT_BUTTON1:
		case DATAPOINT_BUTTON_COLOR1:
		case EVENTS.button1Pressed:
			return button1Element;

		case DATAPOINT_BUTTON2:
		case DATAPOINT_BUTTON_COLOR2:
		case EVENTS.button2Pressed:
			return button2Element;

		case DATAPOINT_LED:
		case DATAPOINT_LED_COLOR:
			return lightElement;

		case DATAPOINT_PIN1:
		case DATAPOINT_PIN2:
		case DATAPOINT_PIN3:
		case DATAPOINT_PIN4:
			return gpioElement;

		case DATAPOINT_IS_ONLINE:
			return statusElement;
	}
};

export const initConditionState = (condition: UiCondition): Promise<RuleConditionState> => {
	const {id, assetUrn, online, frequency, color, status, pressed, compareOperator, counter, duration} = condition;

	if (assetUrn) {
		return getAssetFromCacheOrFetch(assetUrn).then((asset) => {
			const device = findDeviceForAsset(asset);
			const element = device ? findElementOfDeviceByUiCondition(device, condition) : undefined;
			const elementTarget = element ? findElementTargetByUiCondition(condition) : undefined;
			return {
				id,
				asset,
				element,
				elementTarget,
				online,
				color,
				status,
				pressed,
				frequency,
				compareOperator,
				counter,
				duration
			} as RuleConditionState;
		});
	} else {
		return Promise.resolve({
			id,
			asset: undefined,
			element: undefined,
			elementTarget: undefined,
			online,
			color,
			status,
			pressed,
			frequency,
			compareOperator: CompareOperator.equal
		});
	}
};
export const createCondition = (logicOperator: LogicOperator | null | undefined): UiCondition => {
	return {
		id: undefined,
		assetUrn: undefined,
		dataPoint: undefined,
		online: undefined,
		frequency: undefined,
		color: undefined,
		status: undefined,
		pressed: undefined,
		compareOperator: logicOperator ? undefined : CompareOperator.equal,
		logicOperator,
		nestedConditions: undefined,
		type: undefined,
		metadata: {
			uuid: uuid(),
			isValid: false
		}
	};
};

const buildPinInfo = (status: boolean | undefined, frequency: number | undefined): Pin => {
	return {
		status: status || false,
		frequency: frequency || 0
	};
};

const buildButtonInfo = (color: string | undefined, frequency: number | undefined): Button => {
	return {
		color: color || '#00000',
		frequency: frequency || 0
	};
};

const buildLedInfo = (color: string | undefined, frequency: number | undefined): Led => {
	return {
		color: color || '#00000',
		frequency: frequency || 0
	};
};

const buildButtonPressedInfo = (pressed?: boolean): ButtonPressed => {
	return {
		pressed: pressed || false
	};
};

const buildAssetStateContent = (uiCondition: UiCondition): Partial<AssetState> | void => {
	const {assetUrn, dataPoint, online, frequency, color, status, pressed, nestedConditions, counter, duration} =
		uiCondition;
	return nestedConditions && nestedConditions.length
		? undefined
		: {
				urn: assetUrn || '',
				online: dataPoint === DATAPOINT_IS_ONLINE ? online : undefined,
				button1Pressed: dataPoint === EVENTS.button1Pressed ? buildButtonPressedInfo(pressed) : undefined,
				button2Pressed: dataPoint === EVENTS.button2Pressed ? buildButtonPressedInfo(pressed) : undefined,
				button1: dataPoint === DATAPOINT_BUTTON1 ? buildButtonInfo(color, frequency) : undefined,
				button2: dataPoint === DATAPOINT_BUTTON2 ? buildButtonInfo(color, frequency) : undefined,
				led1: dataPoint === DATAPOINT_LED ? buildLedInfo(color, frequency) : undefined,
				pin1: dataPoint === DATAPOINT_PIN1 ? buildPinInfo(status, frequency) : undefined,
				pin2: dataPoint === DATAPOINT_PIN2 ? buildPinInfo(status, frequency) : undefined,
				pin3: dataPoint === DATAPOINT_PIN3 ? buildPinInfo(status, frequency) : undefined,
				pin4: dataPoint === DATAPOINT_PIN4 ? buildPinInfo(status, frequency) : undefined,
				counter: counter
					? {
							conditionCount: counter.conditionCount
					  }
					: undefined,
				duration: duration
					? {
							durationInMin: duration.durationInMin
					  }
					: undefined
		  };
};

const findConditionById = (conditions: UiCondition | null | undefined, id: string) => {
	if (!conditions) {
		return undefined;
	}

	if (conditions.id === id) {
		return conditions;
	} else if (conditions.nestedConditions) {
		return conditions.nestedConditions.find((c) => c.id === id);
	}
};

const findPreviouslySavedUiConditionById = (id?: string, savedUiRule?: UiRule): UiCondition | void => {
	if (!savedUiRule || !id) {
		return undefined;
	}

	return findConditionById(savedUiRule.triggerConditions, id) || findConditionById(savedUiRule.conditions, id);
};

const hasOptionalSetting = (rule: Record<string, any>, type: string) => {
	return (
		rule.triggerConditions &&
		(rule.triggerConditions[type] ||
			(rule.triggerConditions.nestedConditions &&
				rule.triggerConditions.nestedConditions.find((c: Record<string, any>) => !!c[type])))
	);
};

const hasOptionalSettingsInAssetState = (rule: Record<string, any>, type: string) => {
	if (!rule.triggerConditions) {
		return false;
	}

	const hasOptionalSettingInDirectAssetState = !!(
		rule.triggerConditions.assetState && rule.triggerConditions.assetState[type]
	);
	const hasOptionalSettingInNestedAssetState = !!(
		rule.triggerConditions.nestedConditions &&
		rule.triggerConditions.nestedConditions.find((r: Record<string, any>) => r.assetState && r.assetState[type])
	);
	return hasOptionalSettingInDirectAssetState || hasOptionalSettingInNestedAssetState;
};

// only visible for testing
export const mapUiConditionToApiCondition = (uiCondition: UiCondition, savedUiRule?: UiRule): Condition => {
	const {id, nestedConditions: nestedUiConditions, logicOperator, compareOperator} = uiCondition;
	let newId = undefined;
	const assetState = buildAssetStateContent(uiCondition);
	const nestedConditions =
		nestedUiConditions && nestedUiConditions.length
			? nestedUiConditions.map((nestedUiCondition) =>
					mapUiConditionToApiCondition(nestedUiCondition, savedUiRule)
			  )
			: undefined;
	const savedCondition = findPreviouslySavedUiConditionById(id, savedUiRule);

	if (savedCondition) {
		const conditionHasSameAssetState =
			JSON.stringify(buildAssetStateContent(savedCondition)) === JSON.stringify(assetState);
		newId = conditionHasSameAssetState ? savedCondition.id : undefined;
	}

	return {
		id: newId,
		assetState: assetState as AssetState,
		compareOperator,
		nestedConditions,
		logicOperator
	};
};
export const mapApiConditionToUiCondition = (condition: Condition): UiCondition => {
	const {compareOperator, nestedConditions: nestedApiConditions, logicOperator, assetState} = condition;
	const nestedConditions =
		nestedApiConditions && nestedApiConditions.length
			? nestedApiConditions.map((nestedApiCondition) => mapApiConditionToUiCondition(nestedApiCondition))
			: [];
	const uiCondition: UiCondition = {
		id: undefined,
		assetUrn: undefined,
		dataPoint: undefined,
		online: undefined,
		color: undefined,
		status: undefined,
		pressed: undefined,
		frequency: undefined,
		counter: undefined,
		duration: undefined,
		compareOperator,
		logicOperator,
		nestedConditions,
		metadata: {
			uuid: uuid(),
			isValid: true
		}
	};

	if (assetState) {
		const {
			urn,
			online,
			button1Pressed,
			button2Pressed,
			pin1,
			pin2,
			pin3,
			pin4,
			button1,
			button2,
			led1,
			counter,
			duration
		} = assetState;
		uiCondition.id = condition.id;
		uiCondition.counter = counter;
		uiCondition.duration = duration;
		uiCondition.assetUrn = urn;

		if (button1Pressed) {
			uiCondition.dataPoint = EVENTS.button1Pressed;
			const {pressed} = button1Pressed;
			uiCondition.pressed = pressed;
		} else if (button2Pressed) {
			uiCondition.dataPoint = EVENTS.button2Pressed;
			const {pressed} = button2Pressed;
			uiCondition.pressed = pressed;
		} else if (online != undefined) {
			uiCondition.dataPoint = DATAPOINT_IS_ONLINE;
			uiCondition.online = online;
		} else if (pin1) {
			uiCondition.dataPoint = DATAPOINT_PIN1;
			const {frequency, status} = pin1;
			uiCondition.frequency = frequency;
			uiCondition.status = status;
		} else if (pin2) {
			uiCondition.dataPoint = DATAPOINT_PIN2;
			const {frequency, status} = pin2;
			uiCondition.frequency = frequency;
			uiCondition.status = status;
		} else if (pin3) {
			uiCondition.dataPoint = DATAPOINT_PIN3;
			const {frequency, status} = pin3;
			uiCondition.frequency = frequency;
			uiCondition.status = status;
		} else if (pin4) {
			uiCondition.dataPoint = DATAPOINT_PIN4;
			const {frequency, status} = pin4;
			uiCondition.frequency = frequency;
			uiCondition.status = status;
		} else if (button1) {
			uiCondition.dataPoint = DATAPOINT_BUTTON1;
			const {color, frequency} = button1;
			uiCondition.frequency = frequency;
			uiCondition.color = color;
		} else if (button2) {
			uiCondition.dataPoint = DATAPOINT_BUTTON2;
			const {color, frequency} = button2;
			uiCondition.frequency = frequency;
			uiCondition.color = color;
		} else if (led1) {
			uiCondition.dataPoint = DATAPOINT_LED;
			const {color, frequency} = led1;
			uiCondition.frequency = frequency;
			uiCondition.color = color;
		}
	}

	return uiCondition;
};
export const mapUiRuleToApiRule = (uiRule: UiRule, savedUiRule?: UiRule): Rule => {
	const {
		id,
		name,
		active,
		assetGroupId,
		triggerConditions: uiTriggerConditions,
		conditions: uiConditions,
		actions: uiActions,
		deactivatedBySystem,
		apiTriggerEnabled
	} = uiRule;
	const triggerConditions = uiTriggerConditions && mapUiConditionToApiCondition(uiTriggerConditions, savedUiRule);
	const conditions = uiConditions && mapUiConditionToApiCondition(uiConditions, savedUiRule);
	const actions = uiActions.map((uiAction) => mapUiActionToApiAction(uiAction));
	return {
		id: id as string,
		name,
		active,
		assetGroupId,
		triggerConditions,
		conditions,
		actions,
		deactivatedBySystem,
		apiTriggerEnabled
	};
};
export const mapApiRuleToUiRule = (rule: Rule): UiRule => {
	const {
		id,
		name,
		active,
		assetGroupId,
		triggerConditions: apiTriggerConditions,
		conditions: apiConditions,
		actions: uiActions,
		deactivatedBySystem,
		apiTriggerEnabled
	} = rule;
	const triggerConditions = apiTriggerConditions && mapApiConditionToUiCondition(apiTriggerConditions);
	const conditions = apiConditions && mapApiConditionToUiCondition(apiConditions);
	const actions = uiActions.map((uiAction) => mapApiActionToUiAction(uiAction));
	return {
		id,
		name,
		active,
		assetGroupId,
		triggerConditions,
		conditions,
		actions,
		metadata: {
			isValid: true,
			uuid: '---'
		},
		deactivatedBySystem,
		apiTriggerEnabled
	};
};
export const countConditions = (condition: UiCondition | null | undefined): number => {
	if (!condition) {
		return 0;
	} else if (condition.nestedConditions && condition.nestedConditions.length) {
		let nestedCount = 0;
		condition.nestedConditions.forEach((nestedCondition) => (nestedCount += countConditions(nestedCondition)));
		return nestedCount;
	} else {
		// single condition - counts as one
		return 1;
	}
};
export const shouldShowSettingsHeader = (condition?: UiCondition | null | undefined): boolean => {
	if (!condition) {
		return false;
	}

	if (condition.nestedConditions && condition.nestedConditions.length) {
		return !!condition.nestedConditions.find((c) => c.dataPoint);
	}

	return !!condition.dataPoint;
};
export const shouldShowTriggerDetailLink = (
	currentRule: UiRule | null | undefined,
	savedRule: UiRule | null | undefined
) => {
	if (!currentRule || !savedRule) {
		return false;
	}

	const rule1 = mapUiRuleToApiRule(currentRule);
	const rule2 = mapUiRuleToApiRule(savedRule);
	const isSameRule = JSON.stringify(rule1) === JSON.stringify(rule2);
	const hasOptionalCounterSettings = hasOptionalSetting(currentRule, 'counter');
	const hasOptionalDurationSettings = hasOptionalSetting(currentRule, 'duration');
	return isSameRule && (!!hasOptionalCounterSettings || !!hasOptionalDurationSettings);
};
export const ruleHasOptionalSettings = (rule?: Rule) => {
	if (!rule || !rule.triggerConditions) {
		return false;
	}

	const hasCounterSettings = hasOptionalSettingsInAssetState(rule, 'counter');
	const hasDurationSettings = hasOptionalSettingsInAssetState(rule, 'duration');
	return hasCounterSettings || hasDurationSettings;
};
